import { alpha, createTheme } from '@mui/material/styles';
import { brand, dark, light, radius, fontFamily } from './tokens';

// Theme central de PrestaPlus+ (direccion visual A "Marino").
// MUI 5.10 no tiene colorSchemes: hay dos themes, uno claro y otro oscuro.
// Se crean una sola vez a nivel de modulo; crearTema(modo) solo devuelve el que toca.

const construir = (modo) => {
	const t = modo === 'dark' ? dark : light;

	return createTheme({
		palette: {
			mode: modo,
			primary: { main: t.primary, contrastText: t.onPrimary },
			secondary: { main: t.secondary, contrastText: t.onSecondary },
			info: { main: t.info, contrastText: t.onInfo },
			success: { main: t.success, contrastText: t.onSuccess },
			warning: { main: t.warning, contrastText: t.onWarning },
			error: { main: t.error, contrastText: t.onError },
			background: { default: t.background, paper: t.surface },
			text: { primary: t.text, secondary: t.textSecondary },
			divider: t.border,
			// Claves propias: fondos suaves de chip, menu lateral y colores del logo.
			soft: t.soft,
			side: t.side,
			brand
		},
		shape: { borderRadius: radius.small },
		typography: {
			fontFamily,
			button: { textTransform: 'none', fontWeight: 700 },
			// Para montos de dinero: <Typography variant="monto">
			monto: {
				fontFamily,
				fontWeight: 700,
				fontSize: '1rem',
				lineHeight: 1.4,
				fontVariantNumeric: 'tabular-nums'
			}
		},
		components: {
			MuiButton: {
				defaultProps: { disableElevation: true },
				styleOverrides: {
					root: {
						borderRadius: radius.button,
						textTransform: 'none',
						fontWeight: 700
					}
				}
			},
			MuiOutlinedInput: {
				styleOverrides: {
					root: { borderRadius: radius.field }
				}
			},
			// La ayuda de un campo deshabilitado suele explicar por qué lo está ("Elija primero el
			// departamento"): con el gris de deshabilitado no llegaba al contraste mínimo (axe). El error
			// sigue en rojo aunque el campo esté deshabilitado.
			MuiFormHelperText: {
				styleOverrides: {
					root: ({ theme }) => ({
						'&.Mui-disabled:not(.Mui-error)': { color: theme.palette.text.secondary }
					})
				}
			},
			// Tarjetas y superficies: elevacion baja y un borde fino para separar
			// (antes elevacion 20 en todas).
			MuiPaper: {
				defaultProps: { elevation: 1 },
				styleOverrides: {
					root: {
						borderRadius: radius.card,
						backgroundImage: 'none',
						border: `1px solid ${t.border}`
					}
				}
			},
			MuiDialog: {
				styleOverrides: {
					paper: { borderRadius: radius.dialog },
					// Pantalla completa (ServerOff, Unautorized): sin radio ni borde.
					paperFullScreen: { borderRadius: 0, border: 'none' }
				}
			},
			MuiMenu: {
				styleOverrides: {
					paper: { borderRadius: radius.small }
				}
			},
			MuiPopover: {
				styleOverrides: {
					paper: { borderRadius: radius.small }
				}
			},
			// Popup del DesktopDatePicker (@mui/x-date-pickers)
			MuiPickersPopper: {
				styleOverrides: {
					paper: { borderRadius: radius.small }
				}
			},
			MuiAutocomplete: {
				styleOverrides: {
					paper: { borderRadius: radius.small }
				}
			},
			MuiTooltip: {
				styleOverrides: {
					tooltip: {
						borderRadius: radius.small,
						backgroundColor: t.tooltipBg,
						color: t.tooltipText,
						fontSize: '0.8125rem',
						fontWeight: 400
					},
					arrow: { color: t.tooltipBg }
				}
			},

			// Armazon (fase W0): barra superior con la superficie del theme y borde inferior, y menu
			// lateral con el token `side`. Las variables --nav-* las usa links/styles.css para los
			// colores de los items del menu (normal, activo y al pasar el cursor).
			MuiAppBar: {
				styleOverrides: {
					root: {
						backgroundColor: t.surface,
						color: t.text,
						// Sin el velo claro que MUI pone sobre el AppBar oscuro ni la sombra de elevacion 4.
						backgroundImage: 'none',
						boxShadow: 'none',
						borderRadius: 0,
						border: 'none',
						borderBottom: `1px solid ${t.border}`
					}
				}
			},
			MuiDrawer: {
				styleOverrides: {
					paper: {
						backgroundColor: t.side,
						color: t.text,
						borderRadius: 0,
						// Conserva solo el borde derecho propio del drawer (no el del Paper).
						borderTop: 0,
						borderBottom: 0,
						borderLeft: 0,
						'--nav-texto': t.textSecondary,
						'--nav-hover': alpha(t.primary, 0.08),
						'--nav-activo-texto': t.primary,
						'--nav-activo-fondo': t.soft.primary
					}
				}
			}
		}
	});
};

const temas = {
	light: construir('light'),
	dark: construir('dark')
};

/**
 * Devuelve el theme de MUI para el modo pedido.
 * @param {'light'|'dark'} modo
 */
export const crearTema = (modo) => (modo === 'dark' ? temas.dark : temas.light);
